'use client';

import { c as _c } from "react/compiler-runtime";
import { Skeleton } from '@common/components/atom/Skeleton/Skeleton';
import { cn } from '@core/utils';
import type { GiftOrderProductReviewDto } from '@generated/auth';
import { Suspense } from 'react';
import type { GiftProductDetail } from '@/api/getGiftProductDetail';
import { EventRewardCard } from '@/app/donation/_components/EventRewardCard/EventRewardCard';
import { DONATION_DESKTOP_STICKY_TOP } from '@/app/donation/constants';
import type { GiftSaleStatus } from '@/app/donation/types';
import { BREAKPOINTS } from '@/constants/breakpoints';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { TABS } from '../../constants';
import { GiftDetailTab } from '../GiftDetailTab';
import { GiftTabContentBasic } from '../GiftTabContent/_components/GiftTabContentBasic';
import { GiftTabContentDelivery } from '../GiftTabContent/_components/GiftTabContentDelivery';
import { GiftTabContentInfo } from '../GiftTabContent/_components/GiftTabContentInfo';
import { GiftTabContentInquiry } from '../GiftTabContent/_components/GiftTabContentInquiry';
import { GiftTabContentReview } from '../GiftTabContent/_components/GiftTabContentReview';
import { HeaderInfoSection } from './HeaderInfoSection';
import { PrefillForm } from './PrefillForm';
import { ResponsiveDivider } from './ResponsiveDivider';
import { ReviewShortcutSection } from './ReviewShortcutSection';
import { useSelectedDonationAmount } from './useSelectedDonationAmount';
interface GiftDetailTemplateProps {
  title: string;
  heroImageUrlList: string[];
  adultYN: boolean;
  institutionName: string;
  productSaleStatus: GiftSaleStatus;
  productName: string;
  reviewRating?: number;
  reviewCount: number;
  productNumber: string;
  requiredDonationAmount: number;
  onlineProductYN: boolean;
  onlineProductDesc?: string;
  exchangeYN: boolean;
  reviewList: GiftOrderProductReviewDto[];
  reviewTotalCount: number;
  photoReviewList: {
    fileUrl: string;
    reviewId: number;
  }[];
  photoReviewCount: number;
  detailContentInfoList: GiftProductDetail['detailContentInfoList'];
  productInfo: string;
  deliveryCompanyName?: string;
  providerName?: string;
  providerBusinessNumber?: string;
  providerContactNumber?: string;
  providerAddress?: string;
  productData?: GiftProductDetail;
  noticeTitle?: string;
  noticeContents?: string;
}
const GIFT_DETAIL_SIDEBAR_STICKY_TOP = 218;
export const GiftDetailTemplate = t0 => {
  const $ = _c(80);
  const {
    title,
    heroImageUrlList,
    adultYN,
    institutionName,
    productSaleStatus,
    productName,
    reviewCount,
    reviewRating,
    productNumber,
    requiredDonationAmount,
    exchangeYN,
    onlineProductYN,
    onlineProductDesc,
    reviewList,
    reviewTotalCount,
    photoReviewList,
    photoReviewCount,
    detailContentInfoList,
    productInfo,
    deliveryCompanyName,
    providerName,
    providerBusinessNumber,
    providerContactNumber,
    providerAddress,
    productData,
    noticeTitle,
    noticeContents
  } = t0;
  const isDesktopViewport = useMediaQuery(BREAKPOINTS.DESKTOP, true);
  const t1 = productData?.currentPrice;
  let t2;
  if ($[0] !== requiredDonationAmount || $[1] !== t1) {
    t2 = {
      currentPrice: t1,
      requiredDonationAmount
    };
    $[0] = requiredDonationAmount;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    totalDonationAmount
  } = useSelectedDonationAmount(t2);
  let t3;
  if ($[3] !== reviewCount) {
    t3 = [...(reviewCount < 1 ? [TABS.REVIEW] : [])];
    $[3] = reviewCount;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const disabledTabs = t3;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = cn("mt-20 content-container", "tablet:mt-0 tablet:px-0", "mobile:mt-0 mobile:px-0");
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = cn("mb-100 px-40", "mobile-tablet:mb-20 mobile-tablet:px-0");
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== adultYN || $[8] !== exchangeYN || $[9] !== heroImageUrlList || $[10] !== institutionName || $[11] !== onlineProductDesc || $[12] !== onlineProductYN || $[13] !== productData || $[14] !== productName || $[15] !== productNumber || $[16] !== productSaleStatus || $[17] !== requiredDonationAmount || $[18] !== reviewCount || $[19] !== reviewRating || $[20] !== title) {
    t6 = <HeaderInfoSection title={title} heroImageUrlList={heroImageUrlList} adultYN={adultYN} institutionName={institutionName} productSaleStatus={productSaleStatus} productName={productName} reviewCount={reviewCount} reviewRating={reviewRating} productNumber={productNumber} requiredDonationAmount={requiredDonationAmount} exchangeYN={exchangeYN} onlineProductYN={onlineProductYN} onlineProductDesc={onlineProductDesc} productData={productData} className={t5} />;
    $[7] = adultYN;
    $[8] = exchangeYN;
    $[9] = heroImageUrlList;
    $[10] = institutionName;
    $[11] = onlineProductDesc;
    $[12] = onlineProductYN;
    $[13] = productData;
    $[14] = productName;
    $[15] = productNumber;
    $[16] = productSaleStatus;
    $[17] = requiredDonationAmount;
    $[18] = reviewCount;
    $[19] = reviewRating;
    $[20] = title;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = cn("mb-100 px-40", "mobile-tablet:hidden");
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== reviewCount || $[24] !== reviewList) {
    t8 = <ReviewShortcutSection reviewCount={reviewCount} items={reviewList} className={t7} />;
    $[23] = reviewCount;
    $[24] = reviewList;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = cn("sticky z-10 mx-40 bg-Gray-white", "mobile-tablet:top-[48px] mobile-tablet:mx-0");
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== isDesktopViewport) {
    t10 = isDesktopViewport ? {
      top: DONATION_DESKTOP_STICKY_TOP
    } : undefined;
    $[27] = isDesktopViewport;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== disabledTabs || $[30] !== productNumber || $[31] !== t10) {
    t11 = <GiftDetailTab productNumber={productNumber} disabledTabs={disabledTabs} className={t9} style={t10} />;
    $[29] = disabledTabs;
    $[30] = productNumber;
    $[31] = t10;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = cn("mt-40 grid grid-cols-[1fr_370px] gap-80 px-40", "mobile-tablet:mt-0 mobile-tablet:grid-cols-1 mobile-tablet:px-0");
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== detailContentInfoList || $[35] !== noticeContents || $[36] !== noticeTitle || $[37] !== productName) {
    t13 = <GiftTabContentBasic detailContentInfoList={detailContentInfoList} productName={productName} noticeTitle={noticeTitle} noticeContents={noticeContents} />;
    $[34] = detailContentInfoList;
    $[35] = noticeContents;
    $[36] = noticeTitle;
    $[37] = productName;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <ResponsiveDivider />;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  let t15;
  if ($[40] !== productInfo) {
    t15 = <GiftTabContentInfo productInfo={productInfo} />;
    $[40] = productInfo;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  let t16;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <ResponsiveDivider />;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  let t17;
  if ($[43] !== deliveryCompanyName || $[44] !== providerAddress || $[45] !== providerBusinessNumber || $[46] !== providerContactNumber || $[47] !== providerName) {
    t17 = <GiftTabContentDelivery deliveryCompanyName={deliveryCompanyName} providerName={providerName} providerBusinessNumber={providerBusinessNumber} providerContactNumber={providerContactNumber} providerAddress={providerAddress} />;
    $[43] = deliveryCompanyName;
    $[44] = providerAddress;
    $[45] = providerBusinessNumber;
    $[46] = providerContactNumber;
    $[47] = providerName;
    $[48] = t17;
  } else {
    t17 = $[48];
  }
  let t18;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <ResponsiveDivider />;
    $[49] = t18;
  } else {
    t18 = $[49];
  }
  let t19;
  if ($[50] !== photoReviewCount || $[51] !== photoReviewList || $[52] !== productNumber || $[53] !== reviewCount || $[54] !== reviewList || $[55] !== reviewRating || $[56] !== reviewTotalCount) {
    t19 = <GiftTabContentReview reviewRating={reviewRating} reviewCount={reviewCount} reviewList={reviewList} reviewTotalCount={reviewTotalCount} photoReviewList={photoReviewList} photoReviewCount={photoReviewCount} productNumber={productNumber} />;
    $[50] = photoReviewCount;
    $[51] = photoReviewList;
    $[52] = productNumber;
    $[53] = reviewCount;
    $[54] = reviewList;
    $[55] = reviewRating;
    $[56] = reviewTotalCount;
    $[57] = t19;
  } else {
    t19 = $[57];
  }
  let t20;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <ResponsiveDivider />;
    $[58] = t20;
  } else {
    t20 = $[58];
  }
  let t21;
  if ($[59] !== productNumber) {
    t21 = <GiftTabContentInquiry productNumber={productNumber} />;
    $[59] = productNumber;
    $[60] = t21;
  } else {
    t21 = $[60];
  }
  let t22;
  if ($[61] !== t13 || $[62] !== t15 || $[63] !== t17 || $[64] !== t19 || $[65] !== t21) {
    t22 = <div className="flex min-w-0 flex-col">{t13}{t14}{t15}{t16}{t17}{t18}{t19}{t20}{t21}</div>;
    $[61] = t13;
    $[62] = t15;
    $[63] = t17;
    $[64] = t19;
    $[65] = t21;
    $[66] = t22;
  } else {
    t22 = $[66];
  }
  let t23;
  if ($[67] !== isDesktopViewport || $[68] !== productData || $[69] !== productNumber || $[70] !== totalDonationAmount) {
    t23 = isDesktopViewport && <div><div className="sticky mb-44 rounded-[36px] border-1 px-28 py-44" style={{
        top: GIFT_DETAIL_SIDEBAR_STICKY_TOP
      }}><PrefillForm productData={productData} productNumber={productNumber} className="gap-80" /><Suspense fallback={<Skeleton className="mt-24 h-56 w-full rounded-medium" />}><EventRewardCard productNumber={productNumber} donationAmount={totalDonationAmount} defaultExpanded={true} className="mt-24" /></Suspense></div></div>;
    $[67] = isDesktopViewport;
    $[68] = productData;
    $[69] = productNumber;
    $[70] = totalDonationAmount;
    $[71] = t23;
  } else {
    t23 = $[71];
  }
  let t24;
  if ($[72] !== t22 || $[73] !== t23) {
    t24 = <div className={t12}>{t22}{t23}</div>;
    $[72] = t22;
    $[73] = t23;
    $[74] = t24;
  } else {
    t24 = $[74];
  }
  let t25;
  if ($[75] !== t11 || $[76] !== t24 || $[77] !== t6 || $[78] !== t8) {
    t25 = <div className={t4}>{t6}{t8}{t11}{t24}</div>;
    $[75] = t11;
    $[76] = t24;
    $[77] = t6;
    $[78] = t8;
    $[79] = t25;
  } else {
    t25 = $[79];
  }
  return t25;
};